// ConfirmDialog.tsx — ported from SK-Catalog (src/components/confirmDialog.tsx),
// adapted to SK-Tickets tokens (Colors/FontWeight/Radii/FontFamily) and the
// local SVG `Icon` component. Asks BEFORE a destructive/important action; pair
// with SuccessDialog for the "done ✓" moment after.
import React from 'react';
import {
  Modal, View, Text, Pressable, StyleSheet, Dimensions, Platform, TouchableOpacity,
} from 'react-native';
import { Icon } from './Icon';
import { Colors, FontWeight, FontFamily, Radii } from '../theme/tokens';

const { width } = Dimensions.get('window');

type ConfirmDialogProps = Readonly<{
  visible: boolean;            // Controls the visibility of the modal
  title?: string;             // Primary heading text
  message: string;            // Descriptive text explaining the action consequences
  onConfirm: () => void;      // Callback executed upon positive action
  onCancel: () => void;       // Callback executed to dismiss the dialog without changes
  confirmText?: string;       // Custom label for the confirm button
  cancelText?: string;        // Custom label for the cancel button
  confirmColor?: string;      // Themed color for the confirm button and header indicator
  loading?: boolean;          // Disables buttons while the action is in flight
  onClose?: () => void;       // Optional callback for a top-right close icon
  children?: React.ReactNode; // Optional custom content (e.g., input fields)
}>;

export default function ConfirmDialog({
  visible,
  title = 'Confirm Action',
  message,
  onConfirm,
  onCancel,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  confirmColor,
  loading = false,
  onClose,
  children,
}: ConfirmDialogProps) {
  const accentColor = confirmColor ?? Colors.danger; // Destructive default

  return (
    <Modal transparent visible={visible} animationType="fade" onRequestClose={onCancel}>
      <View style={styles.overlay}>
        <View style={styles.box}>
          {/* Header indicator — matches the action type (destructive red by default) */}
          <View style={[styles.indicator, { backgroundColor: accentColor }]} />

          {onClose && (
            <TouchableOpacity
              style={styles.closeButton}
              onPress={onClose}
              hitSlop={{ top: 20, bottom: 20, left: 20, right: 20 }}
            >
              <Icon name="X" size={18} color={Colors.ink4} />
            </TouchableOpacity>
          )}

          <View style={styles.content}>
            <Text style={styles.title}>{title}</Text>
            <Text style={styles.message}>{message}</Text>
            {children}
          </View>

          <View style={styles.footer}>
            <Pressable
              style={({ pressed }) => [styles.btn, styles.cancelBtn, pressed && styles.btnPressed]}
              onPress={onCancel}
              disabled={loading}
            >
              <Text style={styles.cancelText}>{cancelText}</Text>
            </Pressable>

            <Pressable
              style={({ pressed }) => [
                styles.btn,
                { backgroundColor: accentColor },
                (pressed || loading) && styles.btnPressed,
              ]}
              onPress={onConfirm}
              disabled={loading}
            >
              <Text style={styles.confirmText}>{loading ? 'Deleting…' : confirmText}</Text>
            </Pressable>
          </View>
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(15, 23, 42, 0.75)',
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 24,
  },
  box: {
    width: Math.min(width * 0.9, 400),
    backgroundColor: Colors.card,
    borderRadius: Radii.r5,
    overflow: 'hidden',
    // Responsive on web — never wider/taller than the viewport so the full
    // dialog is always visible; the content area scrolls if it's long.
    ...(Platform.OS === 'web' ? { maxWidth: '92vw', maxHeight: '90vh', display: 'flex', flexDirection: 'column' } as any : {}),
    ...Platform.select({
      web: { boxShadow: '0 18px 48px -12px rgba(16,24,40,0.30), 0 4px 12px rgba(16,24,40,0.10)' } as any,
      ios: { shadowColor: '#000', shadowOffset: { width: 0, height: 8 }, shadowOpacity: 0.18, shadowRadius: 16 },
      android: { elevation: 10 },
    }),
  },
  indicator: { height: 4, width: '100%', opacity: 0.85, flexShrink: 0 },
  content: {
    paddingTop: 22, paddingHorizontal: 22, paddingBottom: 18, alignItems: 'center',
    // Let a tall body scroll within the capped box instead of overflowing.
    ...(Platform.OS === 'web' ? { overflowY: 'auto', flexShrink: 1, minHeight: 0 } as any : {}),
  },
  title: {
    fontSize: 17, fontWeight: FontWeight.bold, fontFamily: FontFamily.sans,
    color: Colors.ink, textAlign: 'center', marginBottom: 8, letterSpacing: -0.3,
  },
  message: {
    fontSize: 13, fontFamily: FontFamily.sans, color: Colors.ink3,
    textAlign: 'center', lineHeight: 19,
  },
  footer: { flexDirection: 'row', padding: 14, gap: 10, backgroundColor: Colors.paper2, flexShrink: 0 },
  btn: { flex: 1, height: 42, borderRadius: Radii.r4, justifyContent: 'center', alignItems: 'center' },
  btnPressed: { opacity: 0.8, transform: [{ scale: 0.97 }] },
  cancelBtn: { backgroundColor: Colors.paper3 },
  cancelText: { fontSize: 14, fontWeight: FontWeight.bold, fontFamily: FontFamily.sans, color: Colors.ink2 },
  confirmText: { fontSize: 14, fontWeight: FontWeight.bold, fontFamily: FontFamily.sans, color: Colors.white },
  closeButton: {
    position: 'absolute', top: 15, right: 15, zIndex: 10,
    backgroundColor: Colors.paper2, borderRadius: Radii.full,
    width: 32, height: 32, justifyContent: 'center', alignItems: 'center',
  },
});
